import { wrappingInputRule, mergeAttributes } from '@tiptap/core';
import BulletList from '@tiptap/extension-bullet-list';
import TextStyle from '@tiptap/extension-text-style';
import { MarkdownNodeSpec } from 'tiptap-markdown';
import { BulletListToken } from '@scale/shared/types/config';

/**
 * Matches a bullet list to a dash or asterisk.
 */
export const inputRegex = /^\s*([-+*])\s$/;

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    bullet_list_extended: {
      toggleBulletListWithDefaultToken: () => ReturnType;
    };
  }
}

function getToken(tokenEnum: BulletListToken): string {
  return tokenEnum;
}

export default BulletList.extend({
  addStorage(): { markdown: MarkdownNodeSpec } {
    return {
      markdown: {
        // Source: https://github.com/ProseMirror/prosemirror-markdown/blob/master/src/to_markdown.ts#L133serialize
        serialize(state, node) {
          Object.assign(node, { attrs: { ...node.attrs, tight: true } }); // enforce tight lists
          return state.renderList(node, '  ', () => node.attrs.token + ' ');
        },
        parse: {
          setup(markdownIt) {
            // Custom rendering for 'bullet list' tokens
            markdownIt.renderer.rules.bullet_list_open = (tokens, idx, options, _env, self) => {
              const token = tokens[idx];

              // Check the marker used to create the list
              // Look ahead to the list item token to find the bullet type
              const listItemToken = tokens[idx + 1];
              const markup = listItemToken.markup || ''; // `-`, `*`, or `+`

              // Set the data-delimiter attribute based on the marker
              token.attrSet('token', markup);

              // Render the token with the added attribute
              return self.renderToken(tokens, idx, options);
            };
          },
        },
      },
    };
  },
  addCommands() {
    return {
      ...this.parent?.(),
      toggleBulletListWithDefaultToken:
        () =>
        ({ commands }) => {
          const defaultBulletToken =
            this.editor.storage.markdownExtensions.bulletListToken ?? BulletListToken.ASTERISK;
          commands.toggleList(this.name, this.options.itemTypeName, this.options.keepMarks, {
            token: getToken(defaultBulletToken),
          });
          return true;
        },
    };
  },
  addAttributes() {
    return {
      token: {
        default: '*',
        renderHTML: attributes => {
          return {
            token: attributes.token,
          };
        },
      },
    };
  },

  addInputRules() {
    let inputRule = wrappingInputRule({
      find: inputRegex,
      type: this.type,
      getAttributes: match => {
        return { token: match[1] };
      },
    });

    if (this.options.keepMarks || this.options.keepAttributes) {
      inputRule = wrappingInputRule({
        find: inputRegex,
        type: this.type,
        keepMarks: this.options.keepMarks,
        keepAttributes: this.options.keepAttributes,
        getAttributes: match => {
          return mergeAttributes(this.editor.getAttributes(TextStyle.name), { token: match[1] });
        },
        editor: this.editor,
      });
    }
    return [inputRule];
  },
});
